import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Row, useToken } from "@hightouch/htui";
import { useMediaQuery } from "src/hooks/use-media-query";

export const COMPACT_TOOLBAR_WIDTH = 560;
// Fits all seven secondary icons plus +, dictation, Send, and their gaps.
const MENU_TOOLBAR_WIDTH = 416;

type ToolbarLayout = "labels" | "icons" | "menu";
const ToolbarLayoutContext = createContext<ToolbarLayout>("labels");

export const useToolbarLayout = () => useContext(ToolbarLayoutContext);

export function useCompactToolbarViewport() {
  const navBreakpoint = useToken("breakpoints", "lg");
  // The nav collapses here and makes the composer wider despite a smaller window.
  return useMediaQuery(`(width < ${navBreakpoint})`);
}

/** Selection cannot change the layout: only toolbar and viewport widths do. */
export function ComposerToolbar({
  children,
  gap = 2,
  collapseActions = false,
}: {
  children: ReactNode;
  gap?: number;
  collapseActions?: boolean;
}) {
  const isCompactViewport = useCompactToolbarViewport();
  const ref = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ToolbarLayout>("labels");

  useLayoutEffect(() => {
    if (!collapseActions) return;
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      setLayout(
        width < MENU_TOOLBAR_WIDTH
          ? "menu"
          : isCompactViewport || width < COMPACT_TOOLBAR_WIDTH
            ? "icons"
            : "labels",
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [collapseActions, isCompactViewport]);

  return (
    <ToolbarLayoutContext.Provider value={collapseActions ? layout : "labels"}>
      <Row
        ref={ref}
        align="center"
        justify="space-between"
        gap={gap}
        width="100%"
        minWidth={0}
      >
        {children}
      </Row>
    </ToolbarLayoutContext.Provider>
  );
}

/** Keep picker state mounted while its controls move into icons or the + menu. */
export function ComposerInlineActions({
  children,
  gap = 2,
}: {
  children: ReactNode;
  gap?: number;
}) {
  const layout = useToolbarLayout();
  return (
    <Row
      align="center"
      gap={gap}
      display={layout === "labels" ? "flex" : "none"}
    >
      {children}
    </Row>
  );
}
